import { useCallback, useEffect, useMemo, useState } from 'react'
import Fuse from 'fuse.js'
import { useRouter } from 'next/router'
import {
    CommandDialog,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command'
import {
    filterSettingsSearchIndex,
    type SettingsSearchEntry,
} from '@/lib/settings/settingsSearchIndex'

const SECTION_LABELS: Record<SettingsSearchEntry['section'], string> = {
    brand: 'Brand Center',
    shopper: 'Shopper Experience',
    integrations: 'Integrations',
    account: 'Account',
}

type SettingsSearchDialogProps = {
    open: boolean
    onOpenChange: (open: boolean) => void
    isSuperUser: boolean
    isOrganicClient: boolean
}

export const SettingsSearchDialog = ({
    open,
    onOpenChange,
    isSuperUser,
    isOrganicClient,
}: SettingsSearchDialogProps) => {
    const router = useRouter()
    const [query, setQuery] = useState('')

    const entries = useMemo(
        () =>
            filterSettingsSearchIndex({
                isSuperUser,
                isOrganicClient,
            }),
        [isSuperUser, isOrganicClient],
    )

    const fuse = useMemo(
        () =>
            new Fuse(entries, {
                keys: [
                    { name: 'label', weight: 0.4 },
                    { name: 'keywords', weight: 0.4 },
                    { name: 'description', weight: 0.2 },
                    { name: 'group', weight: 0.1 },
                ],
                threshold: 0.35,
            }),
        [entries],
    )

    const results = useMemo(() => {
        if (!query.trim()) {
            return entries
        }
        return fuse.search(query.trim()).map((result) => result.item)
    }, [entries, fuse, query])

    const groupedResults = useMemo(() => {
        const groups = new Map<string, SettingsSearchEntry[]>()
        for (const entry of results) {
            const key = SECTION_LABELS[entry.section]
            const existing = groups.get(key) ?? []
            existing.push(entry)
            groups.set(key, existing)
        }
        return groups
    }, [results])

    const handleSelect = useCallback(
        (entry: SettingsSearchEntry) => {
            onOpenChange(false)
            setQuery('')
            router.push(entry.href)
        },
        [onOpenChange, router],
    )

    return (
        <CommandDialog open={open} onOpenChange={onOpenChange}>
            <CommandInput
                placeholder="Search settings..."
                value={query}
                onValueChange={setQuery}
            />
            <CommandList>
                <CommandEmpty>No settings found.</CommandEmpty>
                {Array.from(groupedResults.entries()).map(
                    ([groupLabel, groupEntries]) => (
                        <CommandGroup key={groupLabel} heading={groupLabel}>
                            {groupEntries.map((entry) => (
                                <CommandItem
                                    key={entry.id}
                                    value={`${entry.label} ${entry.keywords.join(' ')}`}
                                    onSelect={() => handleSelect(entry)}
                                >
                                    <div className="flex flex-col">
                                        <span>{entry.label}</span>
                                        {entry.description ? (
                                            <span className="text-xs text-muted-foreground">
                                                {entry.description}
                                            </span>
                                        ) : (
                                            <span className="text-xs text-muted-foreground">
                                                {entry.group}
                                            </span>
                                        )}
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    ),
                )}
            </CommandList>
        </CommandDialog>
    )
}

export const useSettingsSearchShortcut = (
    onOpen: () => void,
    enabled: boolean,
) => {
    useEffect(() => {
        if (!enabled) return

        const handleKeyDown = (event: KeyboardEvent) => {
            if (
                (event.metaKey || event.ctrlKey) &&
                event.key.toLowerCase() === 'k'
            ) {
                event.preventDefault()
                onOpen()
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [enabled, onOpen])
}
